import classNames from 'classnames';
import { keyBy } from 'lodash';
import React, { FC, useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { SimpleSearchFilter } from 'api/dashboard/widget';
import { ProjectId, TaskIdV2 } from 'api/models';
import { Column } from 'components/atoms/grid';
import {
  TASK_LIST_COLUMNS_STATE_LOCALSTORAGE_KEY,
  excludedAllTasksFields,
} from 'components/organisms/tasks/constants';
import { FilterGroupType } from 'components/pages/types';
import { RootState } from 'data';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useQueryJiraConfig } from 'queries/jira-integration';
import { ProjectField, TaskField, FieldsStore } from 'stores/fields';
import { useProjectFieldsAsFilterableFields } from 'utils/project-fields/use-project-fields-as-filterable-fields';
import { ColumnOrder, cleanUpColumnOrder } from '../grid-custom-column';
import { useTaskDrawer } from '../task-drawer';
import { DashboardGrid } from '../task-grid/grid';
import { COLUMN_ORDER_SERIALIZER } from './column-order-serializer';
import { Flex, Overlay, OverlayContent, Wrapper } from './components';
import { GroupedFilter } from './grouped-filter-popover';
import { useTaskAndProjectColumns, getDefaultColumnOrder } from './use-columns';
import {
  default_column_order,
  stringToColumnOrder,
  DEFAULT_COLUMN_ORDER,
  useServerSideDataSourceForDashBoard,
} from './utils';

const StyledDashboardGrid = styled(DashboardGrid)`
  .ag-row-group-leaf-indent.ag-row-group-indent-0 {
    padding-left: 0 !important;
  }
`;

const GridWrapper = styled(Wrapper)`
  padding-left: 0 !important;
`;

const Container = styled.div`
  height: calc(100% - 48px);
`;

const EMPTY_RECORD = {};

const DEFAULT_VIEW_ID = -1;

interface DashBoardTasks {
  widgetGroupedFilter: GroupedFilter;
  widgetFilter: SimpleSearchFilter;
  dashboardFilter: SimpleSearchFilter;
  fieldNames: string[];
  widgetId: number;
}

export const DashBoardTasksList: FC<DashBoardTasks> = ({
  widgetFilter,
  widgetGroupedFilter,
  dashboardFilter,
  fieldNames,
  widgetId,
}) => {
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const { data: jiraIntegrationConfig } = useQueryJiraConfig();
  const translate = useTranslate();
  const no_data = useTranslation('all_tasks.no_data');

  const excludedFields = useMemo(() => {
    // remove jiraIntegration from excluded fields ONLY when the integration is active
    if (jiraIntegrationConfig?.length) {
      return excludedAllTasksFields.filter(
        (field) => field !== 'jiraIntegration',
      );
    }

    return excludedAllTasksFields;
  }, [jiraIntegrationConfig]);

  const excludedFieldsForColumns = useMemo(
    () => [
      ...excludedFields,
      'private',
      'completedLateBy',
      'phaseName',
      'taskDependencyStatus',
    ],
    [excludedFields],
  );

  const excludedFieldsForFilters = useMemo(
    () => [...excludedFields, 'projectPhaseId'],
    [excludedFields],
  );

  const [fields] = useMemo(() => {
    const taskFields = FieldsStore.enabledTaskFields(fieldsStore);

    return [
      taskFields.filter(({ fieldName }) => !excludedFields.includes(fieldName)),
      taskFields.filter(
        ({ fieldName }) => !excludedFieldsForFilters.includes(fieldName),
      ),
    ];
  }, [fieldsStore, excludedFields, excludedFieldsForFilters]);
  const { fields: projectFields, loading: isProjectFieldLoading } =
    useProjectFieldsAsFilterableFields();

  const taskFieldsLookupById = useMemo(
    () =>
      keyBy(
        fields.filter(
          ({ fieldName }) => !excludedFieldsForColumns.includes(fieldName),
        ),
        'fieldId',
      ) as Record<number, TaskField>,
    [fields, excludedFieldsForColumns],
  );
  const projectFieldsLookupById = useMemo(
    () =>
      keyBy(isProjectFieldLoading ? [] : projectFields, 'fieldId') as Record<
        number,
        ProjectField
      >,
    [isProjectFieldLoading, projectFields],
  );

  const fieldsLookupForSort = useMemo(
    () =>
      fields.length > 0
        ? (keyBy(fields, 'fieldName') as Record<number, TaskField>)
        : EMPTY_RECORD,
    [fields],
  );
  const fields_loading =
    !FieldsStore.loaded(fieldsStore) || isProjectFieldLoading;

  const includedProjectColumns = useMemo(
    () =>
      widgetGroupedFilter.groupCriteria
        .filter((x) => x.id === FilterGroupType.Project)
        .map((x) => x.criteria.field.fieldName),
    [widgetGroupedFilter.groupCriteria],
  );

  const [columnOrderV2] = useLocalStorage<string[]>(
    TASK_LIST_COLUMNS_STATE_LOCALSTORAGE_KEY,
    default_column_order,
    COLUMN_ORDER_SERIALIZER,
  );

  const masterColumnOrder = useMemo(
    () => columnOrderV2.map(stringToColumnOrder) || DEFAULT_COLUMN_ORDER,
    [columnOrderV2],
  );

  const columns: Column[] = useTaskAndProjectColumns(
    taskFieldsLookupById,
    projectFieldsLookupById,
    translate,
    includedProjectColumns,
  );
  const defaultColumnState: ColumnOrder[] = useMemo(
    () => getDefaultColumnOrder(columns),
    [columns],
  );

  const [order] = useLocalStorage<ColumnOrder[]>(
    `all_tasks.v3.viewId=${DEFAULT_VIEW_ID}.column_order`,
    cleanUpColumnOrder(defaultColumnState)(masterColumnOrder),
  );

  const { onGridReady, gridApi, gridColumnApi, isInitialLoading } =
    useServerSideDataSourceForDashBoard(
      taskFieldsLookupById,
      projectFieldsLookupById,
      dashboardFilter,
      widgetFilter,
      fieldsLookupForSort,
      widgetId,
    );
  const [, show, ,] = useTaskDrawer();

  const onTaskClick = useCallback(
    (id: TaskIdV2, projectId: ProjectId) => {
      show(projectId, id);
    },
    [show],
  );

  useEffect(() => {
    if (!gridColumnApi) {
      return;
    }
    const selectedFields = order.filter((coluumField) =>
      fieldNames.includes(coluumField.colId ?? ''),
    );
    const otherFields = order.filter(
      (coluumField) => !fieldNames.includes(coluumField.colId ?? ''),
    );
    const newColState = [...selectedFields, ...otherFields];

    gridColumnApi.setColumnState(newColState);
  }, [order, fieldNames, gridColumnApi]);

  const [numberOfRows, setNumberOfRows] = useState(
    isInitialLoading ? Number.MAX_VALUE : 0,
  );

  useEffect(() => {
    const updateNumberOfRows = (e) => {
      setNumberOfRows(e.api.getDisplayedRowCount());
    };

    gridApi?.addEventListener('modelUpdated', updateNumberOfRows);

    return () =>
      gridApi?.removeEventListener('modelUpdated', updateNumberOfRows);
  }, [gridApi]);

  if (fields_loading) {
    return null;
  }

  return (
    <Container>
      <GridWrapper>
        <Flex
          className={classNames({
            'ag-theme-balham': true,
          })}
        >
          <StyledDashboardGrid
            fields={fields}
            columns={columns}
            onGridReady={onGridReady}
            suppressClickEdit
            isPaginated
            onTaskClick={onTaskClick}
            suppressMovableColumns
          />
        </Flex>
        {!isInitialLoading && !numberOfRows ? (
          <Overlay>
            <OverlayContent>{no_data}</OverlayContent>
          </Overlay>
        ) : null}
      </GridWrapper>
    </Container>
  );
};
